import React, { ReactNode } from 'react'
import { Banner, bannerHref, bannerSrc } from './banner_types'

/*
 * Generic, site-agnostic banner primitives.
 *
 * `BannerFrame` owns the one behaviour Jake specified for every placement
 * (main / left / right): when `link_url` has a value the WHOLE banner is the
 * click target, and when it does not there is no link wrapper at all — no
 * `href="#"`, no click handler standing in for a link.
 *
 * `BannerImage` owns the src rule and the alt rule.
 */

interface BannerFrameProps {
    banner: Banner
    className?: string
    children: ReactNode
}

export function BannerFrame({ banner, className, children }: BannerFrameProps) {
    const href = bannerHref(banner)

    if (!href) {
        return <div className={className}>{children}</div>
    }

    const newTab = banner.open_in_new_tab === true

    return (
        <a
            className={`${className ?? ''} --linked`.trim()}
            href={href}
            // open_in_new_tab === false stays in the current tab (no target)
            target={newTab ? '_blank' : undefined}
            rel={newTab ? 'noopener noreferrer' : undefined}
        >
            {children}
        </a>
    )
}

interface BannerImageProps {
    banner: Banner
    /** Picks `mobile_image_url ?? image_url` instead of `image_url`. */
    mobile?: boolean
    width?: number
    height?: number
    eager?: boolean
    priority?: 'high' | 'low'
}

export function BannerImage({ banner, mobile = false, width, height, eager, priority }: BannerImageProps) {
    return (
        <img
            src={bannerSrc(banner, mobile)}
            // alt_text is used verbatim — an empty string is a legitimate
            // "decorative image" value and is left alone.
            alt={banner.alt_text ?? ''}
            width={width}
            height={height}
            loading={eager ? undefined : 'lazy'}
            decoding="async"
            // @ts-ignore — fetchPriority landed after this React version's typings
            fetchpriority={priority}
            draggable={false}
        />
    )
}
